import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";
import { toast } from "sonner";

const AU = ["Australia", "AU", "au", "australia"];
const NZ = ["New Zealand", "NZ", "nz", "new zealand"];
const CHOICES = [["Australia", "Australia"], ["New Zealand", "New Zealand"], ["Other", "Somewhere else"]];

export const countrySet = (u) => !!u && (AU.includes((u.country || "").trim()) || NZ.includes((u.country || "").trim())
  || (u.country === "Other" && !!(u.country_other || "").trim()));

export const countryLabel = (u) => (u?.country === "Other" ? u.country_other : u?.country) || "Not set";

export const countryValid = (v) => !!v.country && (v.country !== "Other" || !!v.other.trim());

export function CountryFields({ value, onChange, prefix }) {
  return (
    <div className="space-y-2" role="radiogroup" aria-label="Country">
      <div className="flex flex-wrap gap-2">
        {CHOICES.map(([k, label]) => (
          <button key={k} type="button" role="radio" aria-checked={value.country === k} onClick={() => onChange({ ...value, country: k })}
            data-testid={`${prefix}-country-${k.replace(" ", "-").toLowerCase()}`}
            className={`px-4 py-2 rounded-full border text-sm transition-colors ${value.country === k ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "border-slate-200 text-slate-700 hover:border-[#C9A961]"}`}>
            {label}
          </button>
        ))}
      </div>
      {value.country === "Other" && (
        <Input value={value.other} maxLength={60} placeholder="e.g. United Kingdom" onChange={(e) => onChange({ ...value, other: e.target.value })}
          data-testid={`${prefix}-country-other-input`} className="max-w-xs" />
      )}
    </div>
  );
}

export function CountryChoice({ onSaved, prefix = "country" }) {
  const { user, refresh } = useAuth();
  const [v, setV] = useState({ country: countrySet(user) ? (AU.includes(user.country) ? "Australia" : NZ.includes(user.country) ? "New Zealand" : "Other") : "", other: user?.country_other || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/country", { country: v.country, country_other: v.country === "Other" ? v.other.trim() : null });
      await refresh();
      toast.success("Country saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <CountryFields value={v} onChange={setV} prefix={prefix} />
      <Button onClick={save} disabled={busy || !countryValid(v)} data-testid={`${prefix}-save`} className="rounded-full bg-[#C9A961] text-[#0A1F44] hover:bg-[#b8974f]">Save</Button>
    </div>
  );
}

// Own missing country → one prompt until saved. View As → read-only note only.
export default function CountryPrompt({ onSaved }) {
  const { user, isViewAs } = useAuth();
  if (!user || countrySet(user)) return null;
  if (isViewAs) {
    return <p className="text-xs text-slate-500" data-testid="country-missing-note">This partner hasn't set their country yet.</p>;
  }
  return (
    <Card className="p-5 border-[#C9A961]/50 space-y-3" data-testid="country-prompt">
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#C9A961]" aria-hidden="true" />
        <h2 className="font-serif text-lg text-[#0A1F44]">Which country are you based in?</h2>
      </div>
      <CountryChoice onSaved={onSaved} prefix="country-prompt" />
    </Card>
  );
}
